import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const EMPTY = { day: "Thu", time: "9:00 PM EST", title: "", platform: "Whatnot", sort_order: 100 };

export default function AdminSchedule() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await api.get("/admin/schedule")).data); }
    catch { toast.error("Failed to load"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/schedule", { ...editing, sort_order: parseInt(editing.sort_order, 10) || 0 });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    await api.delete(`/admin/schedule/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-4xl">Live Auction Schedule</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="add-show-btn">
          <Plus size={16} className="inline mr-1 -mt-0.5" /> New Show
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] mb-6 space-y-4" data-testid="show-form">
          <div className="grid md:grid-cols-5 gap-4">
            <Field label="Day"><input value={editing.day} onChange={(e) => setEditing({ ...editing, day: e.target.value })} data-testid="show-day" className="input" /></Field>
            <Field label="Time"><input value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} data-testid="show-time" className="input" /></Field>
            <Field label="Title" className="md:col-span-2"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="show-title" required className="input" /></Field>
            <Field label="Platform"><input value={editing.platform} onChange={(e) => setEditing({ ...editing, platform: e.target.value })} className="input" /></Field>
            <Field label="Sort Order"><input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="input" /></Field>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="show-save">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-2" data-testid="schedule-list">
        {items.length === 0 && <div className="text-zinc-500">No shows scheduled yet.</div>}
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-4 border border-white/10 rounded p-3 bg-[var(--cl-surface)]">
            <div className="font-display text-2xl w-16">{s.day}</div>
            <div className="text-sm text-zinc-400 w-32">{s.time}</div>
            <div className="flex-1 min-w-0">
              <div className="truncate">{s.title}</div>
              <div className="text-xs text-[var(--cl-orange)] mt-0.5">{s.platform}</div>
            </div>
            <button onClick={() => setEditing(s)} className="text-xs btn-outline px-3 py-1">Edit</button>
            <button onClick={() => del(s.id)} className="p-2 hover:text-[var(--cl-primary)]" data-testid={`del-show-${s.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-label text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
